import { Globe, Smartphone, Palette, TrendingUp, Cpu, Cctv } from 'lucide-react'

const img = (id, file) =>
  `https://images.pexels.com/photos/${id}/${file || `pexels-photo-${id}.jpeg`}?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600`

export const SERVICES = [
  {
    slug: 'web-development',
    icon: Globe,
    gradient: 'from-blue-500/10 to-cyan-500/10',
    iconGradient: 'from-blue-600 to-cyan-500',
    hoverBorder: 'hover:border-blue-300/50 group-hover:shadow-blue-500/10',
    accent: 'from-blue-600 to-cyan-500',
    images: {
      hero: img('1181675'),
      heroPosition: 'object-[center_30%]',
      heroAlt: {
        en: 'Developer writing website code on a laptop',
        am: 'ባለሙያ ለድረ-ገጽ ኮድ በላፕቶፕ ላይ እየጻፈ',
      },
      gallery: [
        {
          src: img('546819'),
          position: 'object-center',
          alt: { en: 'Programming code on a computer monitor', am: 'በኮምፒውተር ስክሪን ላይ የፕሮግራም ኮድ' },
          caption: { en: 'Website code written and tested before launch', am: 'ድረ-ገጽ ከመውጣቱ በፊት የሚጻፍ እና የሚሞከር ኮድ' },
        },
        {
          src: img('196644'),
          position: 'object-center',
          alt: { en: 'Website layout sketches and interface design', am: 'የድረ-ገጽ አቀማመጥ እና በይነገጽ ንድፍ' },
          caption: { en: 'Page layouts designed for desktop and phone', am: 'ለኮምፒውተር እና ለስልክ የሚነደፉ የገጽ አቀማመጦች' },
        },
        {
          src: img('1181244'),
          position: 'object-[center_20%]',
          alt: { en: 'Web developer working at a desktop computer', am: 'የድረ-ገጽ አልሚ በኮምፒውተር ላይ እየሰራ' },
          caption: { en: 'From company websites to web applications', am: 'ከኩባንያ ድረ-ገጽ እስከ ዌብ መተግበሪያ' },
        },
      ],
    },
    en: {
      title: 'Web Development',
      shortDesc: 'Custom websites and web applications built with modern technologies',
      tagline: 'A fast, mobile-ready website that explains your business and brings in inquiries.',
      overview: [
        'Fortino Tech designs and develops websites that do more than look good. We build company profiles, clinic sites, landing pages, e-commerce stores, and custom web applications that load quickly, work on every phone, and make it easy for a visitor to call, book, or buy.',
        'We start by understanding how your customers find you and what they need to do on the site. Then we design the pages, write the structure, and develop with proven tools such as React and Node.js. The result is a site you can update, host securely, and grow without starting over.',
        'Every project includes mobile-first layout, basic search-engine setup, secure hosting guidance, and training so your team can manage content. After launch we remain available for updates, backups, and 24/7 support.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'Companies that need a professional online profile in Addis Ababa and across Ethiopia',
        'Clinics, schools, NGOs, and hotels that want bookings or inquiries online',
        'Retail and wholesale businesses ready to sell products on the web',
        'Teams replacing an outdated site that is slow, unsecured, or not mobile-friendly',
      ],
      includedTitle: 'What is included',
      included: [
        'Discovery workshop to capture goals, pages, and brand direction',
        'Custom UI design for home, about, services, and conversion pages',
        'Responsive development that works on phones, tablets, and desktops',
        'Contact forms connected to your email and our admin inbox',
        'SEO foundations: titles, headings, fast loading, and clean URLs',
        'Hosting, domain, and SSL setup support',
        'Admin training and a simple handover document',
        'Post-launch support and maintenance options',
      ],
      processTitle: 'How we deliver a website',
      process: [
        { title: 'Listen and plan', text: 'We map your services, audience, and the actions you want visitors to take.' },
        { title: 'Design', text: 'You review page layouts and visual direction before any coding begins.' },
        { title: 'Build', text: 'We develop the site, connect forms, and prepare content structure.' },
        { title: 'Test', text: 'We check phones, browsers, speed, and every form before you go live.' },
        { title: 'Launch', text: 'We publish on a secure host, connect your domain, and hand over logins.' },
        { title: 'Support', text: 'We stay available for fixes, content help, and future features.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'A site that matches your brand instead of a generic template',
        'More inquiries because calls-to-action and contact paths are clear',
        'A foundation you can expand into a portal, shop, or booking system',
        'Ownership of the code and content, with documentation',
      ],
      toolsTitle: 'Tools we use',
      tools: ['React', 'Node.js', 'JavaScript', 'Tailwind CSS', 'REST APIs', 'Secure hosting'],
      duration: '2–4 weeks',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Discovery and content', duration: '3–5 days', detail: 'We collect brand files, page list, and the actions visitors should take.' },
        { phase: 'Design review', duration: '4–7 days', detail: 'You approve layouts for home, about, services, and contact before coding.' },
        { phase: 'Development and content', duration: '7–14 days', detail: 'We build pages, connect forms, and place your text and photos.' },
        { phase: 'Test, launch, and training', duration: '2–4 days', detail: 'We test phones and browsers, publish the site, and train your team.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'Business name, logo, colors, and any existing brand files',
        'A list of pages and the services you want explained',
        'Text and photos, or a contact who can approve content',
        'Domain name and preferred hosting, or permission for us to set them up',
        'One decision-maker for design and content approval',
        'Access to current website files if we are rebuilding an existing site',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'How long does a typical website take?',
          a: 'A standard company website usually takes 2 to 4 weeks. A larger custom web application can take 4 to 8 weeks after we agree on the features.',
        },
        {
          q: 'Can you rebuild our existing site?',
          a: 'Yes. We can redesign it, keep the same domain, and migrate useful content so you do not lose what already works.',
        },
        {
          q: 'Will we be able to update the site ourselves?',
          a: 'Yes. We set up a practical way for your team to change text, photos, and news, and we train you before handover.',
        },
      ],
    },
    am: {
      title: 'የዌብሳይት ልማት (Web Development)',
      shortDesc: 'በዘመናዊ ቴክኖሎጂዎች የተገነቡ ብጁ ዌብሳይቶች እና የዌብ መተግበሪያዎች',
      tagline: 'ንግድዎን በግልጽ የሚያብራራ እና ደንበኞችን የሚስብ ፈጣንና ለስልክ ተስማሚ ዌብሳይት።',
      overview: [
        'ፎርቲኖ ቴክ ከውበት ባለፈ ውጤታማ የሆኑ ዌብሳይቶችን ይነድፋል፣ ያበለጽጋል። የኩባንያ መገለጫዎችን፣ የሕክምና ክሊኒክ ድረ-ገጾችን፣ የማስተዋወቂያ ገጾችን (landing pages)፣ የኦንላይን ሱቆችን እና ፈጣን፣ በሁሉም ስልኮች ላይ የሚሰሩ፣ እንዲሁም ጎብኚዎች በቀላሉ እንዲደውሉ፣ እንዲያስይዙ ወይም እንዲገዙ የሚያስችሉ ብጁ የዌብ መተግበሪያዎችን እንገነባለን።',
        'ሥራችንን የምንጀምረው ደንበኞችዎ እንዴት እንደሚያገኙዎት እና በዌብሳይቱ ላይ ምን ማድረግ እንደሚፈልጉ በመረዳት ነው። በመቀጠል ገጾቹን እንነድፋለን፣ መዋቅሩን እናዘጋጃለን፣ እና እንደ React እና Node.js ባሉ የተረጋገጡ መሣሪያዎች እናበለጽጋለን። ውጤቱም በቀላሉ ማሻሻል የሚችሉት፣ በታማኝነት የሚያስተናግዱት እና እንደገና መጀመር ሳያስፈልግዎት ሊያሳድጉት የሚችሉት ዌብሳይት ይሆናል።',
        'እያንዳንዱ ፕሮጀክት ለስልክ ቅድሚያ የሚሰጥ አቀማመጥን፣ የመሠረታዊ የፍለጋ ሞተር (SEO) ማስተካከያን፣ ደህንነቱ የተጠበቀ የሆስቲንግ መመሪያን እና ቡድንዎ ይዘቶችን ማስተዳደር እንዲችል ሥልጠናን ያካትታል። ከስራ ማስጀመሪያ በኋላም ቢሆን ለማሻሻያዎች፣ ለባክአፕ (backup) እና ለ24/7 ድጋፍ ዝግጁ ነን።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን ነው?',
      audience: [
        'በአዲስ አበባ እና በጠቅላላ ኢትዮጵያ ሙያዊ የኦንላይን መገለጫ (profile) የሚያስፈልጋቸው ኩባንያዎች',
        'በኦንላይን ቀጠሮ ወይም ጥያቄዎችን መቀበል የሚፈልጉ ክሊኒኮች፣ ትምህርት ቤቶች፣ መንግስታዊ ያልሆኑ ድርጅቶች (NGOs) እና ሆቴሎች',
        'ምርቶቻቸውን በዌብሳይት ለመሸጥ የተዘጋጁ የችርቻሮ እና የጅምላ ንግድ ድርጅቶች',
        'ዘመናዊ ያልሆነ፣ ቀርፋፋ፣ ደህንነቱ ያልተጠበቀ ወይም ለስልክ ያልተመቸ ዌብሳይታቸውን መቀየር የሚፈልጉ ቡድኖች',
      ],
      includedTitle: 'ምን ያካተተ ነው?',
      included: [
        'የንግድ ዓላማዎችን፣ አስፈላጊ ገጾችን እና የብራንድ አቅጣጫን ለመወሰን የሚደረግ የግንዛቤ አውደ ጥናት',
        'ለዋና ገጽ፣ ስለ እኛ፣ አገልግሎቶች እና ደንበኛ የሚስቡ ገጾች የተዘጋጀ ብጁ የUI ዲዛይን',
        'በስልኮች፣ ታብሌቶች እና ኮምፒውተሮች ላይ የሚሰራ ተስማሚ (responsive) ልማት',
        'ከኢሜልዎ እና ከእኛ የአድሚን ኢንቦክስ ጋር የተያያዙ የመገናኛ ቅጾች (Contact forms)',
        'የመሠረታዊ SEO ግንባታ፡ ርዕሶች፣ ዋና ርዕሰ ዜናዎች፣ ፈጣን ጭነት እና ንጹህ URLዎች',
        'የሆስቲንግ፣ ዶሜይን እና SSL ማዋቀር ድጋፍ',
        'የአድሚን ሥልጠና እና ቀላል የካረከረብ ሰነድ',
        'ከምረቃ በኋላ ያሉ የድጋፍ እና የእንክብካቤ አማራጮች',
      ],
      processTitle: 'ዌብሳይት እንዴት እንገነባለን?',
      process: [
        { title: 'ማዳመጥ እና ማቀድ', text: 'የእርስዎን አገልግሎቶች፣ ደንበኞች እና ጎብኚዎች እንዲያደርጉት የሚፈልጉትን እርምጃ እንቀርጻለን።' },
        { title: 'ዲዛይን', text: 'ምንም አይነት ኮድ መጻፍ ከመጀመሩ በፊት የገጽ አቀማመጦችን እና የእይታ አቅጣጫዎችን ገምግመው ያጸድቃሉ።' },
        { title: 'መገንባት', text: 'ዌብሳይቱን እናበለጽጋለን፣ ቅጾችን እናያይዛለን፣ እና የይዘት መዋቅርን እናዘጋጃለን።' },
        { title: 'መፈተሽ', text: 'ወደ ሥራ ከመግባትዎ በፊት ስልኮችን፣ ብራውዘሮችን፣ ፍጥነትን እና እያንዳንዱን ቅጽ እንፈትሻለን።' },
        { title: 'መመረቅ (Launch)', text: 'ደህንነቱ የተጠበቀ ሆስቲንግ ላይ እንለቃለን፣ ዶሜይንዎን እናያይዛለን፣ እና የመግቢያ አካውንቶችን እናስረክባለን።' },
        { title: 'ድጋፍ', text: 'ለእርማቶች፣ ለይዘት ድጋፍ እና ለቀጣይ ተጨማሪ አዳዲስ ባህሪያት ሁልጊዜ ዝግጁ ነን።' },
      ],
      benefitsTitle: 'ምን ያተርፋሉ?',
      benefits: [
        'ከአጠቃላይ የሞዴል ገጾች (templates) ይልቅ ከብራንድዎ ጋር የሚስማማ ብጁ ዌብሳይት',
        'የጥሪ እና የመገናኛ መንገዶች ግልጽ ስለሆኑ ከፍተኛ የደንበኞች ጥያቄ እና ምላሽ',
        'ወደ ፖርታል፣ ሱቅ ወይም የቀጠሮ መያዣ ሥርዓት ሊያሳድጉት የሚችሉት ጠንካራ መሠረት',
        'ከነሰነዱ የኮዱ እና የይዘቱ ሙሉ ባለቤትነት',
      ],
      benefitsToolsTitle: 'ምን እንጠቀማለን?',
      tools: ['React', 'Node.js', 'JavaScript', 'Tailwind CSS', 'REST APIs', 'Secure hosting'],
      duration: 'ከ2 እስከ 4 ሳምንታት',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'ግንዛቤ እና ይዘት', duration: 'ከ3–5 ቀናት', detail: 'የብራንድ ፋይሎችን፣ የገጾችን ዝርዝር እና ጎብኚዎች ማድረግ ያለባቸውን እርምጃዎች እንሰበስባለን።' },
        { phase: 'የዲዛይን ግምገማ', duration: 'ከ4–7 ቀናት', detail: 'ኮድ ከመጻፉ በፊት የዋና ገጽ፣ ስለ እኛ፣ አገልግሎቶች እና መገናኛ ገጽ አቀማመጦችን ያጸድቃሉ።' },
        { phase: 'ልማት እና ይዘት', duration: 'ከ7–14 ቀናት', detail: 'ገጾችን እንገነባለን፣ ቅጾችን እናያይዛለን፣ እና ጽሑፎችዎንና ፎቶዎችዎን እንሰቅላለን።' },
        { phase: 'ፍተሻ፣ ምረቃ እና ሥልጠና', duration: 'ከ2–4 ቀናት', detail: 'በስልኮችና ብራውዘሮች ላይ እንፈትሻለን፣ ዌብሳይቱን እንለቃለን፣ እና ለቡድንዎ ሥልጠና እንሰጣለን።' },
      ],
      requirementsTitle: 'ሥራውን ለመጀመር ከእርስዎ የምንፈልጋቸው ነገሮች',
      requirements: [
        'የንግዱ ስም፣ ሎጎ፣ ቀለሞች እና ማንኛውም ነባር የብራንድ ፋይሎች',
        'የገጾች ዝርዝር እና እንዲብራሩ የሚፈልጓቸው አገልግሎቶች',
        'ጽሑፎች እና ፎቶዎች፣ ወይም ይዘትን ማጽደቅ የሚችል ተወካይ',
        'የዶሜይን ስም እና የሚመርጡት ሆስቲንግ፣ ወይም እኛ እንድንጨርሰው ፈቃድ መስጠት',
        'ለዲዛይን እና ይዘት ማጽደቅ ኃላፊነት ያለው አንድ ውሳኔ ሰጪ ሰው',
        'ነባር ዌብሳይትን የምንቀይር ከሆነ የቆየው ዌብሳይት ፋይሎች መዳረሻ (Access)',
      ],
      faqsTitle: 'የተለመዱ ጥያቄዎች',
      faqs: [
        {
          q: 'መደበኛ ዌብሳይት ምን ያህል ጊዜ ይወስዳል?',
          a: 'መደበኛ የኩባንያ ዌብሳይት ከአብዛኛው ከ2 እስከ 4 ሳምንታት ይወስዳል። ትልልቅ ብጁ የዌብ መተግበሪያዎች ደግሞ በባህሪያቱ ላይ ከተስማማን በኋላ ከ4 እስከ 8 ሳምንታት ሊወስዱ ይችላሉ።',
        },
        {
          q: 'ነባር ዌብሳይታችንን እንደገና ማስተካከል (rebuild) ትችላላችሁ?',
          a: 'አዎ። ያንኑ ዶሜይን ይዘን፣ የሚሰሩ ይዘቶችን ሳያጡ እና ሳይበላሹ ዲዛይኑን አድሰን ሙሉ በሙሉ ማስተካከል እንችላለን።',
        },
        {
          q: 'ዌብሳይቱን ራሳችን ማሻሻል/ማዘመን እንችላለን?',
          a: 'አዎ። ቡድንዎ ጽሑፎችን፣ ፎቶዎችን እና ዜናዎችን በቀላሉ መቀየር የሚችልበትን መንገድ እንዘረጋለን፣ እንዲሁም ከርክክብ በፊት ሥልጠና እንሰጣለን።',
        },
      ],
    },
  },
  {
    slug: 'application-development',
    icon: Smartphone,
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconGradient: 'from-emerald-600 to-teal-500',
    hoverBorder: 'hover:border-emerald-300/50 group-hover:shadow-emerald-500/10',
    accent: 'from-emerald-600 to-teal-500',
    images: {
      hero: img('1092644'),
      heroPosition: 'object-[center_20%]',
      heroAlt: {
        en: 'Smartphone showing mobile app icons in a person’s hand',
        am: 'በእጅ ላይ የሞባይል መተግበሪያዎችን የሚያሳይ ስማርት ስልክ',
      },
      gallery: [
        {
          src: img('607812'),
          position: 'object-center',
          alt: { en: 'Close-up of a smartphone used for a business app', am: 'ለንግድ መተግበሪያ የሚውል ስማርት ስልክ' },
          caption: { en: 'App screens built for phones people use every day', am: 'ሰዎች በየቀኑ ለሚጠቀሙት ስልክ የተሰሩ ስክሪኖች' },
        },
        {
          src: img('887751'),
          position: 'object-[center_25%]',
          alt: { en: 'Person using a mobile app on a smartphone', am: 'ሰው በስማርት ስልክ ላይ መተግበሪያ እየተጠቀመ' },
          caption: { en: 'Android and iOS apps for staff or customers', am: 'ለሰራተኛ ወይም ለደንበኛ የአንድሮይድ እና iOS መተግበሪያዎች' },
        },
        {
          src: img('47261'),
          position: 'object-center',
          alt: { en: 'Hands using a smartphone application', am: 'በስልክ መተግበሪያ ላይ የሚሰሩ እጆች' },
          caption: { en: 'Tested on real phones before release', am: 'ከመውጣቱ በፊት በእውነተኛ ስልኮች የሚሞከር' },
        },
      ],
    },
    en: {
      title: 'Application Development',
      shortDesc: 'Mobile and desktop applications tailored to your needs',
      tagline: 'Custom mobile and desktop apps that put your operations in your team’s pocket.',
      overview: [
        'We build applications around the way your organization actually works. That includes Android and iOS apps, desktop tools, and internal systems for sales, inventory, clinic records, field reporting, or customer self-service.',
        'Instead of forcing you into a ready-made product, we gather the workflows, roles, and reports you need, then design screens that staff can learn quickly. We connect the app to your website, database, or existing software when integration is required.',
        'Delivery includes testing on real devices, store or internal-distribution setup, and a support window so issues are fixed after people start using the app every day.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'Businesses that need a branded app for customers',
        'Clinics, logistics, and field teams that work away from a desk',
        'Companies replacing paper forms or disconnected spreadsheets',
        'Organizations that need a private internal app rather than a public website',
      ],
      includedTitle: 'What is included',
      included: [
        'Requirements workshop and user-role mapping',
        'UI design for the main journeys in the app',
        'Mobile and/or desktop development',
        'Login, permissions, and data protection basics',
        'API or database integration where needed',
        'Device testing and performance checks',
        'Play Store / App Store or internal install guidance',
        'Handover, training, and maintenance options',
      ],
      processTitle: 'How we deliver an application',
      process: [
        { title: 'Map the work', text: 'We document who uses the app, what they do, and which data must be stored.' },
        { title: 'Prototype', text: 'You review screen flow so we agree on the experience before full build.' },
        { title: 'Develop', text: 'We build features in stages you can see and comment on.' },
        { title: 'Integrate', text: 'We connect payments, SMS, websites, or existing systems if required.' },
        { title: 'Test and release', text: 'Staff try the app, we fix issues, then we publish or install it.' },
        { title: 'Improve', text: 'After launch we add the next features based on real use.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'One place for staff or customers to complete tasks',
        'Fewer lost papers and fewer duplicate records',
        'A product that can grow with new modules instead of a full rewrite',
        'Local support from a team that already knows your system',
      ],
      toolsTitle: 'Tools we use',
      tools: ['React Native', 'React', 'Node.js', 'Python', 'REST APIs', 'Secure databases'],
      duration: '4–8 weeks',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Requirements and roles', duration: '4–7 days', detail: 'We map users, workflows, reports, and which data the app must store.' },
        { phase: 'Prototype', duration: '5–10 days', detail: 'You review screen flow before full development begins.' },
        { phase: 'Build and integrate', duration: '3–6 weeks', detail: 'We develop features in stages and connect existing systems where needed.' },
        { phase: 'Test, release, and training', duration: '5–8 days', detail: 'Staff try the app on real devices, then we publish or install it.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'A description of the work the app must do, by user role',
        'Sample forms, reports, or the current spreadsheet/paper process',
        'Brand assets if the app is customer-facing',
        'Access to the current website or system if integration is required',
        'A Google Play / Apple developer account, or permission for us to guide setup',
        'One product owner who can approve screens and features',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'Do you build both Android and iPhone apps?',
          a: 'Yes. We can deliver for both platforms, or start with the one your customers use most.',
        },
        {
          q: 'Can the app connect to our current website or system?',
          a: 'Yes. Integration is a standard part of our work when your data already lives somewhere else.',
        },
        {
          q: 'Do you also build desktop applications?',
          a: 'Yes. If your team works on computers more than phones, we can build a desktop or web app instead of, or together with, a mobile app.',
        },
      ],
    },
    am: {
      title: 'የመተግበሪያ ልማት (Application Development)',
      shortDesc: 'ከፍላጎትዎ ጋር የተጣጣሙ የሞባይል እና የዴስክቶፕ መተግበሪያዎች',
      tagline: 'የድርጅትዎን የሥራ እንቅስቃሴ በቡድንዎ እጅ የሚያስገቡ ብጁ የሞባይል እና የዴስክቶፕ መተግበሪያዎች።',
      overview: [
        'መተግበሪያዎችን የምንገነባው ድርጅትዎ በእርግጥ የሚሰራበትን መንገድ መሠረት በማድረግ ነው። ይህም የአንድሮይድ (Android) እና የiOS መተግበሪያዎችን፣ የዴስክቶፕ ሶፍትዌሮችን፣ እንዲሁም ለሽያጭ፣ ለቁጥጥር (inventory)፣ ለክሊኒክ መዝገቦች፣ የመስክ ሪፖርቶች ወይም ለደንበኞች ራስ-አገልግሎት የሚሆኑ የውስጥ ስርዓቶችን ያካትታል።',
        'ከተዘጋጁ ዝግጁ ምርቶች ጋር እንዲጣጣሙ ከማስገደድ ይልቅ፣ የሚፈልጓቸውን የሥራ ሂደቶች፣ የባለሙያ ድርሻዎች እና ሪፖርቶች በመሰብሰብ ሰራተኞች በቀላሉ ሊለምዱት የሚችሉትን የስክሪን ገጽታዎች እንነድፋለን። አስፈላጊ ሆኖ ሲገኝ መተግበሪያውን ከዌብሳይትዎ፣ ከዳታቤዝዎ ወይም ነባር ሶፍትዌርዎ ጋር እናያይዛለን።',
        'አገልግሎቱ በእውነተኛ መሳሪያዎች ላይ መፈተሽን፣ በGoogle Play/App Store ወይም በውስጥ አውታረ መረብ የመጫን (installation) ማስተካከያን፣ እና ሰዎች መተግበሪያውን በየቀኑ መጠቀም ሲጀምሩ የሚፈጠሩ ችግሮች የሚፈቱበትን የድጋፍ ጊዜ ያካትታል።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን ነው?',
      audience: [
        'ለደንበኞቻቸው የራሳቸው ብራንድ ያለው መተግበሪያ የሚያስፈልጋቸው ንግዶች',
        'ከቢሮ ውጭ የሚሰሩ ክሊኒኮች፣ የሎጂስቲክስ እና የመስክ ቡድኖች',
        'የወረቀት ቅጾችን ወይም የተበታተኑ ኤክሴሎችን (spreadsheets) መተካት የሚፈልጉ ኩባንያዎች',
        'ከህዝባዊ ዌብሳይት ይልቅ የግል የውስጥ መተግበሪያ የሚያስፈልጋቸው ድርጅቶች',
      ],
      includedTitle: 'ምን ያካተተ ነው?',
      included: [
        'የፍላጎቶች አውደ ጥናት እና የተጠቃሚ ድርሻዎችን (roles) የመለየት ሥራ',
        'በመተግበሪያው ውስጥ ላሉ ዋና ዋና ተግባራት የተዘጋጀ የUI ዲዛይን',
        'የሞባይል እና/ወይም የዴስክቶፕ መተግበሪያ ልማት',
        'የመግቢያ (Login)፣ የፈቃድ ደረጃዎች እና የመረጃ ደህንነት መሠረቶች',
        'አስፈላጊ ሆኖ ሲገኝ የAPI ወይም የዳታቤዝ ትስስር',
        'የመሣሪያዎች ፍተሻ እና የአፈጻጸም (performance) ማረጋገጫ',
        'በPlay Store / App Store የመልቀቅ ወይም የውስጥ መጫን መመሪያ',
        'ርክክብ፣ ሥልጠና እና የጥገና አማራጮች',
      ],
      processTitle: 'መተግበሪያን እንዴት እንገነባለን?',
      process: [
        { title: 'ሥራውን ማቀድ', text: 'መተግበሪያውን ማን እንደሚጠቀምበት፣ ምን እንደሚያደርጉ እና የትኛው መረጃ መከማቸት እንዳለበት እናዘጋጃለን።' },
        { title: 'ፕሮቶታይፕ (Prototype)', text: 'ሙሉ ግንባታው ከመጀመሩ በፊት የተጠቃሚውን ልምድ ለማጽደቅ የስክሪን ሂደቶችን ገምግመው ያጸድቃሉ።' },
        { title: 'ማበልጸግ', text: 'ባህሪያቱን አይተው አስተያየት መስጠት በሚችሉባቸው ደረጃዎች እንገነባለን።' },
        { title: 'ማቀናጀት (Integrate)', text: 'አስፈላጊ ሆኖ ሲገኝ የክፍያ ስርዓቶችን፣ ኤስኤምኤስ፣ ዌብሳይቶችን ወይም ነባር ስርዓቶችን እናያይዛለን።' },
        { title: 'መፈተሽ እና መልቀቅ', text: 'ሰራተኞች መተግበሪያውን ይሞክሩታል፣ ችግሮችን እንፈታለን፣ ከዚያም እናትማለን ወይም እንጭናለን።' },
        { title: 'ማሻሻል', text: 'ከምረቃ በኋላ በእውነተኛ አጠቃቀም ላይ በመመስረት ቀጣዮቹን ተጨማሪ ባህሪያት እንጨምራለን።' },
      ],
      benefitsTitle: 'ምን ያተርፋሉ?',
      benefits: [
        'ሰራተኞች ወይም ደንበኞች ተግባራቸውን የሚያከናውኑበት አንድ ማዕከላዊ ቦታ',
        'የጠፉ ወረቀቶች እና የተደገሙ መዝገቦች መቀነስ',
        'ሙሉ በሙሉ እንደገና መጻፍ ሳያስፈልገው ከአዳዲስ ሞጁሎች ጋር ሊያድግ የሚችል ምርት',
        'ስርዓትዎን ቀድሞ የሚያውቅ ቡድን የሚሰጠው የቅርብ (ሀገር ውስጥ) ድጋፍ',
      ],
      toolsTitle: 'ምን እንጠቀማለን?',
      tools: ['React Native', 'React', 'Node.js', 'Python', 'REST APIs', 'Secure databases'],
      duration: 'ከ4 እስከ 8 ሳምንታት',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'ፍላጎቶች እና ድርሻዎች', duration: 'ከ4–7 ቀናት', detail: 'ተጠቃሚዎችን፣ የሥራ ሂደቶችን፣ ሪፖርቶችን እና መተግበሪያው ማከማቸት ያለበትን መረጃ እንለያለን።' },
        { phase: 'ፕሮቶታይፕ', duration: 'ከ5–10 ቀናት', detail: 'ሙሉ ልማቱ ከመጀመሩ በፊት የስክሪን ሂደቶችን ገምግመው ያጸድቃሉ።' },
        { phase: 'ግንባታ እና ማቀናጀት', duration: 'ከ3–6 ሳምንታት', detail: 'ባህሪያቱን በደረጃ እንገነባለን፣ አስፈላጊ ሆኖ ሲገኝም ነባር ስርዓቶችን እናያይዛለን።' },
        { phase: 'ፍተሻ፣ ምረቃ እና ሥልጠና', duration: 'ከ5–8 ቀናት', detail: 'ሰራተኞች መተግበሪያውን በእውነተኛ መሳሪያዎች ይሞክሩታል፣ ከዚያም እናትማለን ወይም እንጭናለን።' },
      ],
      requirementsTitle: 'ሥራውን ለመጀመር ከእርስዎ የምንፈልጋቸው ነገሮች',
      requirements: [
        'መተግበሪያው ማከናወን ያለበት ሥራ መግለጫ፣ በተጠቃሚ ድርሻ የተከፋፈለ',
        'የናሙና ቅጾች፣ ሪፖርቶች፣ ወይም የአሁኑ የኤክሴል/የወረቀት አሰራር ሂደት',
        'መተግበሪያው ለደንበኞች የሚቀርብ ከሆነ የብራንድ ፋይሎች (assets)',
        'ትስስር (integration) የሚያስፈልግ ከሆነ የነባር ዌብሳይት ወይም ስርዓት መዳረሻ',
        'የGoogle Play / Apple አልሚ (developer) አካውንት፣ ወይም እኛ እንድንረዳዎት ፈቃድ መስጠት',
        'ስክሪኖችን እና ባህሪያትን ማጽደቅ የሚችል አንድ የምርት ባለቤት (product owner)',
      ],
      faqsTitle: 'የተለመዱ ጥያቄዎች',
      faqs: [
        {
          q: 'ሁለቱንም የአንድሮይድ እና የiPhone መተግበሪያዎችን ትገነባላችሁ?',
          a: 'አዎ። ለሁለቱም መድረኮች ማቅረብ እንችላለን፣ ወይም ደንበኞችዎ የበለጠ በሚጠቀሙበት መጀመር እንችላለን።',
        },
        {
          q: 'መተግበሪያው ከአሁኑ ዌብሳይታችን ወይም ስርዓታችን ጋር መገናኘት ይችላል?',
          a: 'አዎ። መረጃዎ ቀድሞውኑ ሌላ ቦታ ካለ ማቀናጀትና ማያያዝ የመደበኛ ሥራችን አካል ነው።',
        },
        {
          q: 'የዴስክቶፕ መተግበሪያዎችንስ ትገነባላችሁ?',
          a: 'አዎ። ቡድንዎ ከስልክ ይልቅ በኮምፒውተር ላይ አብዛኛውን ሥራ የሚያከናውን ከሆነ ከሞባይል መተግበሪያ ጋር ወይም በብቸኝነት የዴስክቶፕ ወይም የዌብ መተግበሪያ መገንባት እንችላለን።',
        },
      ],
    },
  },
  {
    slug: 'graphics-design',
    icon: Palette,
    gradient: 'from-rose-500/10 to-orange-500/10',
    iconGradient: 'from-rose-600 to-orange-500',
    hoverBorder: 'hover:border-rose-300/50 group-hover:shadow-rose-500/10',
    accent: 'from-rose-600 to-orange-500',
    images: {
      hero: img('196645'),
      heroPosition: 'object-center',
      heroAlt: {
        en: 'Color pencils and graphic design tools on a studio desk',
        am: 'በስቱዲዮ ጠረጴዛ ላይ የቀለም እርሳሶች እና የግራፊክስ መሳሪያዎች',
      },
      gallery: [
        {
          src: img('326503'),
          position: 'object-[center_25%]',
          alt: { en: 'Graphic designer creating brand artwork on a computer', am: 'የግራፊክስ ዲዛይነር የብራንድ ስራ በኮምፒውተር ላይ እየሰራ' },
          caption: { en: 'Logo and identity designed on screen', am: 'በስክሪን ላይ የሚነደፍ ሎጎ እና ማንነት' },
        },
        {
          src: img('6476589'),
          position: 'object-center',
          alt: { en: 'Designer reviewing visual brand materials', am: 'ዲዛይነር የብራንድ ቁሳቁሶችን እየገመገመ' },
          caption: { en: 'Print, social, and signage from one visual system', am: 'ህትመት፣ ማህበራዊ ሚዲያ እና ምልክት ከአንድ የእይታ ስርዓት' },
        },
        {
          src: img('326501'),
          position: 'object-center',
          alt: { en: 'Graphic design workspace with tablet and artwork', am: 'ታብሌት እና የንድፍ ስራ ያለው የግራፊክስ የስራ ቦታ' },
          caption: { en: 'Files prepared for both print and digital use', am: 'ለህትመት እና ለዲጂታል የተዘጋጁ ፋይሎች' },
        },
      ],
    },
    en: {
      title: 'Graphics Design',
      shortDesc: 'Creative visual design for brands and marketing materials',
      tagline: 'A visual identity people recognize on your wall, your website, and your social posts.',
      overview: [
        'Graphics design at Fortino Tech is the work of making your organization look consistent and trustworthy. We create logos, color systems, stationery, banners, social templates, product mockups, and the visual language used on your website and app.',
        'We do not treat design as decoration. Each piece is built so your name, colors, and tone stay the same whether someone sees a Facebook post, a clinic sign, or a quotation PDF. That consistency is what turns a company into a brand people remember.',
        'You receive source files, export-ready versions for print and digital, and a short usage guide so future posts and print jobs stay on brand even when we are not in the room.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'New businesses that need a logo and a complete first brand kit',
        'Established companies whose materials look different on every channel',
        'Teams launching a website or app and needing matching UI graphics',
        'Campaigns that need posters, event backdrops, and social packs',
      ],
      includedTitle: 'What is included',
      included: [
        'Brand discovery: audience, competitors, and visual direction',
        'Logo concepts and a refined final mark',
        'Color, type, and layout rules',
        'Business cards, letterheads, and quotation templates when needed',
        'Social media templates and cover images',
        'Website or app visual assets',
        'Print-ready and web-ready file handover',
        'A short brand-use guide for your team',
      ],
      processTitle: 'How we deliver design',
      process: [
        { title: 'Brief', text: 'We collect references, must-have elements, and where the design will be used.' },
        { title: 'Concepts', text: 'You compare a small set of directions instead of one rushed option.' },
        { title: 'Refine', text: 'We tighten the chosen direction until the mark and layouts feel right.' },
        { title: 'Apply', text: 'We extend the identity across the materials you actually need.' },
        { title: 'Prepare files', text: 'You get organized folders for print, web, and social.' },
        { title: 'Guide', text: 'We explain how to keep future work consistent.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'A look that feels intentional rather than assembled from random templates',
        'Faster future design work because the system already exists',
        'Print and digital files that match instead of clashing',
        'A brand that supports sales, not only decoration',
      ],
      toolsTitle: 'Tools we use',
      tools: ['Adobe Illustrator', 'Photoshop', 'Figma', 'Brand kits', 'Print-ready PDFs', 'Social templates'],
      duration: '1–3 weeks',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Brief and references', duration: '1–3 days', detail: 'We collect where the design will be used and examples you like or want to avoid.' },
        { phase: 'Concepts', duration: '3–5 days', detail: 'You compare a small set of directions instead of one rushed option.' },
        { phase: 'Refinement', duration: '3–7 days', detail: 'We tighten the chosen logo, colors, and layouts until they feel right.' },
        { phase: 'Application and files', duration: '3–7 days', detail: 'We apply the identity to the materials you need and hand over print and digital files.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'Business name and a short description of what you do',
        'Any existing logo, colors, or materials you want to keep or replace',
        'Where the design will be used: signage, website, social, print, or packaging',
        'Two or three visual references you like, and anything you want to avoid',
        'Exact sizes if you already know print or social formats',
        'One person who can approve concepts and the final files',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'Can you only design a logo?',
          a: 'Yes, we can start with a logo. We also recommend a small kit so the logo is not used differently on every post.',
        },
        {
          q: 'Do you design for both print and social media?',
          a: 'Yes. We prepare the sizes and formats each channel needs so you are not cropping awkwardly later.',
        },
        {
          q: 'Will the design match our website?',
          a: 'If we are also building the site, the identity and the website are designed together. If the site already exists, we match it or refresh both.',
        },
      ],
    },
    am: {
      title: 'የግራፊክስ ዲዛይን (Graphics Design)',
      shortDesc: 'ለብራንዶች እና ማስታወቂያዎች የሚሆን ፈጠራ የታከለበት የእይታ ዲዛይን',
      tagline: 'በግድግዳዎ፣ በዌብሳይትዎ እና በማህበራዊ ሚዲያ ፖስቶችዎ ላይ ሰዎች በቀላሉ የሚያውቁት የእይታ መታወቂያ።',
      overview: [
        'በፎርቲኖ ቴክ የግራፊክስ ዲዛይን አገልግሎት ድርጅትዎን ወጥ የሆነና ተማኝ የሚያስመስል ሥራ ነው። ሎጎዎችን፣ የቀለም ስርዓቶችን፣ የመልእክት ወረቀቶችን (stationery)፣ ባነሮችን፣ የማህበራዊ ሚዲያ ቴምፕሌቶችን፣ የምርት ሞዴሎችን (mockups)፣ እንዲሁም በዌብሳይትዎ እና መተግበሪያዎ ላይ የሚጠቀሙበትን የእይታ ቋንቋ እንፈጥራለን።',
        'ዲዛይንን እንደ ተራ ጌጥ አናየውም። እያንዳንዱ ሥራ የሚሰራው ደንበኛ የፌስቡክ ፖስት፣ የክሊኒክ ቦርድ፣ ወይም የዋጋ ዝርዝር (PDF) ቢያይም ስምዎ፣ ቀለሞችዎ እና አቀራረብዎ ወጥ እና ተመሳሳይ እንዲሆኑ ነው። ይህ ወጥነት ነው ኩባንያን ሰዎች ወደሚያስታውሱት ብራንድ የሚቀይረው።',
        'ለህትመት እና ለዲጂታል የሚሆኑ ዝግጁ ኤክስፖርት ፋይሎችን፣ ምንጭ ፋይሎችን (source files)፣ እና እኛ በሌለንበት ጊዜ እንኳ የወደፊት ፖስቶች እና የህትመት ስራዎች ከብራንዱ ሳይወጡ እንዲሰሩ የሚያስችል አጭር የአጠቃቀም መመሪያ ይረከባሉ።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን ነው?',
      audience: [
        'ሎጎ እና ሙሉ የመጀመሪያ ብራንድ ፓኬጅ የሚያስፈልጋቸው አዳዲስ ንግዶች',
        'የማስታወቂያ ስራዎቻቸው በየቦታው የተለያየ መልክ የሚታይባቸው ነባር ኩባንያዎች',
        'ዌብሳይት ወይም መተግበሪያ ለማስጀመር እና የሚመጥኑ የUI ግራፊክሶች ለሚያስፈልጓቸው ቡድኖች',
        'ፖስተሮች፣ የእንግዳ መቀበያ ባነሮች እና የማህበራዊ ሚዲያ ፓኬጆች የሚያስፈልጓቸው ዘመቻዎች (campaigns)',
      ],
      includedTitle: 'ምን ያካተተ ነው?',
      included: [
        'የብራንድ ማወቅ ሥራ፡ ተመልካቾች፣ ተወዳዳሪዎች እና የእይታ አቅጣጫ',
        'የሎጎ ሀሳቦች (concepts) እና የተስተካከለ የመጨረሻ መለያ (logo)',
        'የቀለም፣ የጽሁፍ ቅርጽ (typography) እና የአቀማመጥ ህጎች',
        'አስፈላጊ ሲሆን የንግድ ካርዶች፣ የደብዳቤ መጻፊያዎች እና የዋጋ መጠየቂያ ቴምፕሌቶች',
        'የማህበራዊ ሚዲያ ቴምፕሌቶች እና የከቨር ምስሎች (cover images)',
        'የዌብሳይት ወይም የመተግበሪያ የእይታ ግራፊክሶች (visual assets)',
        'ለህትመት እና ለዌብሳይት ዝግጁ የሆኑ ፋይሎች ርክክብ',
        'ለቡድንዎ የሚሆን አጭር የብራንድ አጠቃቀም መመሪያ',
      ],
      processTitle: 'ዲዛይን እንዴት እንገነባለን?',
      process: [
        { title: 'መረጃ መሰብሰብ (Brief)', text: 'የሚወዷቸውን ምሳሌዎች፣ አስፈላጊ አካላትን እና ዲዛይኑ የት እንደሚ ጥቅም እንሰበስባለን።' },
        { title: 'ሀሳቦች (Concepts)', text: 'በተጣደፈ አንድ አማራጭ ምትክ ጥቂት የተመረጡ አቅጣጫዎችን ያወዳድራሉ።' },
        { title: 'ማስተካከል', text: 'ምልክቱ እና አቀማመጦቹ ትክክለኛ ስሜት እስኪሰጡ ድረስ የተመረጠውን አቅጣጫ እናሰፋዋለን።' },
        { title: 'ትግበራ', text: 'መታወቂያውን በእርግጥ በሚፈልጓቸው የማስታወቂያ ቁሳቁሶች ላይ እንተገብረዋለን።' },
        { title: 'ፋይሎችን ማዘጋጀት', text: 'ለህትመት፣ ለዌብ እና ለማህበራዊ ሚዲያ የተደራጁ ፎልደሮችን ያገኛሉ።' },
        { title: 'መመሪያ መስጠት', text: 'የወደፊት ስራዎችን እንዴት ወጥ አድርገው ማስቀጠል እንደሚችሉ እናስረዳለን።' },
      ],
      benefitsTitle: 'ምን ያተርፋሉ?',
      benefits: [
        'ከተደባለቁ ቴምፕሌቶች ከተሰበሰበ ይልቅ የታሰበበት እና ሙያዊ የሆነ እይታ',
        'ስርዓቱ ቀድሞ የተዘረጋ በመሆኑ ፈጣን የወደፊት የዲዛይን ስራዎች',
        'እርስ በእርስ የሚጋጩ ሳይሆኑ የሚጣጣሙ የህትመት እና የዲጂታል ፋይሎች',
        'ከጌጥ ባሻገር ሽያጭን የሚያግዝ ጠንካራ ብራንድ',
      ],
      toolsTitle: 'ምን እንጠቀማለን?',
      tools: ['Adobe Illustrator', 'Photoshop', 'Figma', 'Brand kits', 'Print-ready PDFs', 'Social templates'],
      duration: 'ከ1 እስከ 3 ሳምንታት',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'መረጃ እና ምሳሌዎች', duration: 'ከ1–3 ቀናት', detail: 'ዲዛይኑ የት እንደሚ ጥቅም እና የሚወዷቸውን ወይም ሊወገዱ የሚገባቸውን ምሳሌዎች እንሰበስባለን።' },
        { phase: 'ሀሳቦች (Concepts)', duration: 'ከ3–5 ቀናት', detail: 'በተጣደፈ አንድ አማራጭ ምትክ ጥቂት የተመረጡ አቅጣጫዎችን ያወዳድራሉ።' },
        { phase: 'ማስተካከል (Refinement)', duration: 'ከ3–7 ቀናት', detail: 'የተመረጠው ሎጎ፣ ቀለሞች እና አቀማመጦች ትክክለኛ ስሜት እስኪሰጡ ድረስ እናስተካክላለን።' },
        { phase: 'ትግበራ እና ፋይሎች', duration: 'ከ3–7 ቀናት', detail: 'መታወቂያውን በሚፈልጓቸው ቁሳቁሶች ላይ እንተገብራለን፣ እንዲሁም የህትመት እና የዲጂታል ፋይሎችን እናስረክባለን።' },
      ],
      requirementsTitle: 'ሥራውን ለመጀመር ከእርስዎ የምንፈልጋቸው ነገሮች',
      requirements: [
        'የንግዱ ስም እና የሚያደርጉት ነገር አጭር መግለጫ',
        'ማቆየት ወይም መተካት የሚፈልጉት ማንኛውም ነባር ሎጎ፣ ቀለሞች ወይም ቁሳቁሶች',
        'ዲዛይኑ የት እንደሚ ጥቅም፡ ምልክቶች/ቦርዶች፣ ዌብሳይት፣ ማህበራዊ ሚዲያ፣ ህትመት ወይም ማሸጊያዎች (packaging)',
        'የሚወዷቸው ሁለት ወይም ሶስት የእይታ ምሳሌዎች፣ እና ማስወገድ የሚፈልጉት ማንኛውም ነገር',
        'የህትመት ወይም የማህበራዊ ሚዲያ መጠኖችን ቀድመው የሚያውቁ ከሆነ ትክክለኛ அளወች',
        'ሀሳቦችን እና የመጨረሻ ፋይሎችን ማጽደቅ የሚችል አንድ ሰው',
      ],
      faqsTitle: 'የተለመዱ ጥያቄዎች',
      faqs: [
        {
          q: 'ሎጎ ብቻ መዲዛይን ትችላላችሁ?',
          a: 'አዎ፣ በሎጎ መጀመር እንችላለን። ነገር ግን ሎጎው በየፖስቱ ላይ በተለያየ መንገድ እንዳይበላሽ ትንሽ የብራንድ ፓኬጅ (kit) እንመክራለን።',
        },
        {
          q: 'ለህትመትም ሆነ ለማህበራዊ ሚዲያ ዲዛይን ታደርጋላችሁ?',
          a: 'አዎ። በኋላ ላይ ምስሎች በስህተት እንዳይቆረጡ እያንዳንዱ አማራጭ የሚያስፈልገውን መጠን እና ፎርማት እናዘጋጃለን።',
        },
        {
          q: 'ዲዛይኑ ከዌብሳይታችን ጋር ይጣጣማል?',
          a: 'ዌብሳይቱንም እኛ የምንገነባው ከሆነ፣ ብራንዱ እና ዌብሳይቱ አብረው ይነደፋሉ። ዌብሳይቱ ቀድሞ ካለ ደግሞ ከእሱ ጋር እናጣጥመዋለን ወይም ሁለቱንም እናድሳቸዋለን።',
        },
      ],
    },
  },
  {
    slug: 'digital-marketing',
    icon: TrendingUp,
    gradient: 'from-purple-500/10 to-pink-500/10',
    iconGradient: 'from-purple-600 to-pink-500',
    hoverBorder: 'hover:border-purple-300/50 group-hover:shadow-purple-500/10',
    accent: 'from-purple-600 to-pink-500',
    images: {
      hero: img('265087'),
      heroPosition: 'object-[center_35%]',
      heroAlt: {
        en: 'Laptop dashboard showing digital marketing analytics',
        am: 'የዲጂታል ግብይት ትንታኔ በላፕቶፕ ላይ',
      },
      gallery: [
        {
          src: img('905163'),
          position: 'object-center',
          alt: { en: 'Social media marketing on a smartphone', am: 'በስልክ ላይ የማህበራዊ ሚዲያ ግብይት' },
          caption: { en: 'Facebook and Instagram posts with a clear offer', am: 'ግልጽ ቅናሽ ያላቸው የፌስቡክ እና ኢንስታግራም ፖስቶች' },
        },
        {
          src: img('267389'),
          position: 'object-center',
          alt: { en: 'Marketing campaign planning with a megaphone and notes', am: 'የግብይት ዘመቻ እቅድ ከሜጋፎን እና ማስታወሻ ጋር' },
          caption: { en: 'A planned campaign, not one-off posts', am: 'አንድ ጊዜ ፖስት ሳይሆን የታቀደ ዘመቻ' },
        },
        {
          src: img('590016'),
          position: 'object-center',
          alt: { en: 'Growth charts used to measure marketing results', am: 'ለግብይት ውጤት የሚለኩ የእድገት ገበታዎች' },
          caption: { en: 'Results tracked as inquiries and calls', am: 'ውጤት እንደ ጥያቄ እና ጥሪ የሚከታተል' },
        },
      ],
    },
    en: {
      title: 'Digital Marketing',
      shortDesc: 'Strategic marketing to grow your online presence',
      tagline: 'A practical plan to be found online and turn attention into real inquiries.',
      overview: [
        'Digital marketing at Fortino Tech is the work of putting your services in front of the right people and giving them a reason to contact you. That includes search visibility, social media, content, paid ads, and the landing pages those campaigns send people to.',
        'We do not sell empty follower counts. We agree on a goal first: more clinic appointments, more product orders, more quote requests, or stronger brand recognition. Then we choose the channels that can actually reach your customers in Ethiopia and measure what happens.',
        'You receive a simple reporting rhythm so you can see what was published, what people did, and what we will do next. The work stays connected to your website and brand so the message does not change from post to page.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'Businesses that have a website but almost no inquiries from it',
        'Clinics, schools, and shops that want a consistent social presence',
        'Brands launching a new service and needing a campaign, not one post',
        'Teams that are posting randomly and want a monthly plan instead',
      ],
      includedTitle: 'What is included',
      included: [
        'Audience and competitor snapshot',
        'Channel plan: search, social, content, and ads as needed',
        'Monthly content calendar and creative direction',
        'On-page SEO improvements for key service pages',
        'Ad setup and monitoring when paid reach is useful',
        'Landing-page recommendations so traffic is not wasted',
        'Basic analytics and a plain-language monthly summary',
        'Coordination with our design and web teams',
      ],
      processTitle: 'How we run marketing',
      process: [
        { title: 'Set the goal', text: 'We define the inquiry or sale you want, not a vague idea of “being online”.' },
        { title: 'Audit', text: 'We review your current site, pages, and social accounts.' },
        { title: 'Plan', text: 'You approve a calendar and the offers we will promote.' },
        { title: 'Publish', text: 'We produce and post content, and we improve pages that rank or convert.' },
        { title: 'Measure', text: 'We track reach, clicks, and actual messages or calls.' },
        { title: 'Adjust', text: 'We keep what works and stop what does not.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'A visible presence instead of a silent website',
        'Content that matches your real services and tone',
        'A loop between posts, ads, and the pages that collect inquiries',
        'Decisions based on results, not guesswork',
      ],
      toolsTitle: 'Tools we use',
      tools: ['Google Business', 'SEO', 'Facebook & Instagram', 'Content calendars', 'Ads Manager', 'Analytics'],
      duration: '2 weeks to start, then monthly',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Goal and audit', duration: '3–5 days', detail: 'We agree the inquiry you want and review your current site and social accounts.' },
        { phase: 'Plan and calendar', duration: '4–7 days', detail: 'You approve the channels, offers, and the first month of content.' },
        { phase: 'Setup and first publish', duration: '5–10 days', detail: 'We prepare pages, profiles, and the first posts or ads.' },
        { phase: 'Monthly cycle', duration: 'Ongoing', detail: 'Each month we publish, measure inquiries, and keep what works.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'A clear goal: appointments, orders, quote requests, or brand visibility',
        'Website or landing page, or permission for us to prepare one',
        'Logo, photos, and a short description of your services',
        'Access to current Facebook, Instagram, or Google Business accounts',
        'Who can approve posts and how quickly they can reply',
        'Any offers, working hours, and locations we should promote',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'Do we need a website before marketing?',
          a: 'A website or a focused landing page is strongly recommended. Otherwise ads and posts send people somewhere that cannot convert them.',
        },
        {
          q: 'Do you run paid ads?',
          a: 'Yes, when paid reach is the fastest way to the goal. We still start with a clear offer and a page that can receive the traffic.',
        },
        {
          q: 'Can you manage our social media every month?',
          a: 'Yes. We can plan, design, and publish on a monthly cycle, with a report you can actually read.',
        },
      ],
    },
    am: {
      title: 'ዲጂታል ማርኬቲንግ (Digital Marketing)',
      shortDesc: 'የኦንላይን ህልውናዎን ለማሳደግ የሚረዳ ስልታዊ ማርኬቲንግ',
      tagline: 'በኦንላይን በቀላሉ ለመገኘት እና ትኩረትን ወደ እውነተኛ የደንበኛ ጥያቄዎች ለመቀየር የሚረዳ ተግባራዊ እቅድ።',
      overview: [
        'በፎርቲኖ ቴክ የዲጂታል ማርኬቲንግ አገልግሎት አገልግሎቶችዎን በትክክለኛ ደንበኞች ፊት ማቅረብ እና ሰዎችን እንዲያነጋግሩዎት ምክንያት መስጠት ነው። ይህም በፍለጋ ሞተሮች ላይ መገኘትን (SEO)፣ ማህበራዊ ሚዲያን፣ ይዘት ማዘጋጀትን፣ የሚከፈልባቸው ማስታወቂያዎችን (paid ads) እና እነዚያ ማስታወቂያዎች ሰዎችን የሚልኩባቸውን ማስተዋወቂያ ገጾች (landing pages) ያካትታል።',
        'እኛ ትርጉም የሌለውን የፎሎወር ቁጥር አንሸጥም። በመጀመሪያ ግብ ላይ እንስማማለን፡ ተጨማሪ የክሊኒክ ቀጠሮዎች፣ ተጨማሪ የምርት ትዕዛዞች፣ ተጨማሪ የዋጋ መጠየቂያዎች ወይም ጠንካራ የብራንድ እውቅና። በመቀጠል በኢትዮጵያ ውስጥ ያሉ ደንበኞችዎን በእርግጥ ሊደርሱ የሚችሉ ማህበራዊ ሚዲያዎችን እና መድረኮችን መርጠን የሚመጣውን ውጤት እንለካለን።',
        'የታተሙ ነገሮችን፣ ሰዎች ምን እንዳደረጉ እና ቀጥለን ምን እንደምናደርግ ማየት የሚችሉበትን ቀላል ወርሃዊ የሪፖርት አቀራረብ ይደርስዎታል። መልእክቱ ከፖስት እስከ ገጽ ሳይለዋወጥ ወጥ ሆኖ እንዲቀጥል ሥራው ከዌብሳይትዎ እና ከብራንድዎ ጋር የተያያዘ ይሆናል።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን ነው?',
      audience: [
        'ዌብሳይት እያላቸው ነገር ግን ምንም አይነት ደንበኛ ወይም ጥያቄ የማያገኙ ንግዶች',
        'ወጥ የሆነ የማህበራዊ ሚዲያ እንቅስቃሴ የሚፈልጉ ክሊኒኮች፣ ትምህርት ቤቶች እና ሱቆች',
        'አዲስ አገልግሎት የሚጀምሩ እና ከአንድ ፖስት በላይ ሰፊ የፕሮሞሽን ዘመቻ የሚያስፈልጋቸው ብራንዶች',
        'በዘደባቂ ሁኔታ ፖስት የሚያደርጉ እና ይልቁንም ወርሃዊ እቅድ የሚያስፈልጋቸው ቡድኖች',
      ],
      includedTitle: 'ምን ያካተተ ነው?',
      included: [
        'የተመልካቾች እና የተወዳዳሪዎች ፈጣን ዳሰሳ',
        'የመድረኮች እቅድ፡ እንደ አስፈላጊነቱ ፍለጋ (SEO)፣ ማህበራዊ ሚዲያ፣ ይዘት እና ማስታወቂያዎች',
        'ወርሃዊ የይዘት መርሃ ግብር (content calendar) እና የፈጠራ አቅጣጫ',
        'ለዋና ዋና የአገልግሎት ገጾች የሚደረግ የOn-page SEO ማሻሻያ',
        'የሚከፈልበት ማስታወቂያ አስፈላጊ ሲሆን የማስታወቂያ ማዋቀር እና ክትትል',
        'የሚመጣው የደንበኛ ፍሰት እንዳይባክን የላንዲንግ ፔጅ (Landing page) ምክረ ሃሳቦች',
        'መሠረታዊ አናሊቲክስ (analytics) እና በቀላል ቋንቋ የተዘጋጀ ወርሃዊ ማጠቃለያ',
        'ከእኛ የዲዛይን እና የዌብ ቡድኖች ጋር የሚደረግ ቅንጅት',
      ],
      processTitle: 'ማርኬቲንግን እንዴት እንመራለን?',
      process: [
        { title: 'ግብ ማስቀመጥ', text: 'በቀላሉ "ኦንላይን መገኘት" በሚል ግልጽ ያልሆነ ሀሳብ ምትክ የሚፈልጉትን ጥያቄ ወይም ሽያጭ እንወስናለን።' },
        { title: 'ኦዲት (Audit)', text: 'የአሁኑን ዌብሳይትዎን፣ ገጾችዎን እና የማህበራዊ ሚዲያ አካውንቶችዎን እንገመግማለን።' },
        { title: 'ማቀድ', text: 'የምናስተዋውቃቸውን ቅናሾች/አገልግሎቶች እና መርሃ ግብሩን ገምግመው ያጸድቃሉ።' },
        { title: 'ማተም (Publish)', text: 'ይዘቶችን እናዘጋጃለን፣ እንፖስታለን፣ እንዲሁም ሰዎችን የሚስቡ እና ደረጃቸውን የሚጠብቁ ገጾችን እናሻሽላለን።' },
        { title: 'መገምገም (Measure)', text: 'የሰዎችን ተreach፣ ክሊኮች እና እውነተኛ መልእክቶችን ወይም ስልኮችን እንከታተላለን።' },
        { title: 'ማስተካከል', text: 'የሰራውን እናስቀጥላለን፣ ያልሰራውን ደግሞ እናቆማለን።' },
      ],
      benefitsTitle: 'ምን ያተርፋሉ?',
      benefits: [
        'ዝም ካለ ዌብሳይት ይልቅ በግልጽ የሚታይ የኦንላይን ህልውና',
        'ከእውነተኛ አገልግሎቶችዎ እና አቀራረብዎ ጋር የሚስማማ ይዘት',
        'በፖስቶች፣ በማስታወቂያዎች እና የደንበኛ ጥያቄዎችን በሚቀበሉ ገጾች መካከል ያለ የተቀናጀ ትስስር',
        'በግምት ሳይሆን በውጤት ላይ የተመሰረቱ ውሳኔዎች',
      ],
      toolsTitle: 'ምን እንጠቀማለን?',
      tools: ['Google Business', 'SEO', 'Facebook & Instagram', 'Content calendars', 'Ads Manager', 'Analytics'],
      duration: 'ለመጀመር 2 ሳምንታት፣ ከዚያም ወርሃዊ',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'ግብ እና ኦዲት', duration: 'ከ3–5 ቀናት', detail: 'በሚፈልጉት የደንበኛ ጥያቄ አይነት ላይ እንስማማለን፣ እንዲሁም የአሁኑን ዌብሳይትዎንና ማህበራዊ ሚዲያዎን እንገመግማለን።' },
        { phase: 'እቅድ እና መርሃ ግብር', duration: 'ከ4–7 ቀናት', detail: 'መድረኮችን፣ የሚስተዋወቁ ነገሮችን እና የመጀመሪያውን ወር ይዘት ያጸድቃሉ።' },
        { phase: 'ማዋቀር እና የመጀመሪያ ምረቃ', duration: 'ከ5–10 ቀናት', detail: 'ገጾችን፣ ፕሮፋይሎችን እና የመጀመሪያዎቹን ፖስቶች ወይም ማስታወቂያዎች እናዘጋጃለን።' },
        { phase: 'ወርሃዊ ዑደት', duration: 'ቀጣይነት ያለው', detail: 'በየወሩ እናትማለን፣ የደንበኞችን ጥያቄ እንለካለን፣ እንዲሁም ጥሩ የሰራውን እናስቀጥላለን።' },
      ],
      requirementsTitle: 'ሥራውን ለመጀመር ከእርስዎ የምንፈልጋቸው ነገሮች',
      requirements: [
        'ግልጽ ግብ፡ ቀጠሮዎች፣ ትዕዛዞች፣ የዋጋ መጠየቂያዎች ወይም የብራንድ እውቅና',
        'ዌብሳይት ወይም ላንዲንግ ፔጅ፣ ወይም እኛ እንድናዘጋጅልዎ ፈቃድ መስጠት',
        'ሎጎ፣ ፎቶዎች እና የአገልግሎቶችዎ አጭር መግለጫ',
        'የአሁኑ የFacebook፣ Instagram ወይም Google Business አካውንቶች መዳረሻ (Access)',
        'ፖስቶችን ማጽደቅ የሚችል ሰው እና ምን ያህል ፈጣን ምላሽ መስጠት እንደሚችሉ',
        'ማስተዋወቅ የምንፈልጋቸው ማንኛውም ቅናሾች፣ የሥራ ሰዓቶች እና አድራሻዎች',
      ],
      faqsTitle: 'የተለመዱ ጥያቄዎች',
      faqs: [
        {
          q: 'ማርኬቲንግ ከመጀመራችን በፊት ዌብሳይት ያስፈልገናል?',
          a: 'ዌብሳይት ወይም ትኩረት ያደረገ ላንዲንግ ፔጅ እንዲኖርዎት አጥብቀን እንመክራለን። ያለበለዚያ ማስታወቂያዎች እና ፖስቶች ሰዎችን ደንበኛ ወደማያደርግ ቦታ ይወስዷቸዋል።',
        },
        {
          q: 'የሚከፈልባቸው ማስታወቂያዎችን (paid ads) ታሰራጫላችሁ?',
          a: 'አዎ፣ የሚከፈልበት ማስታወቂያ ወደ ግቡ ለመድረስ ፈጣኑ መንገድ ሲሆን እንጠቀማለን። ሆኖም አሁንም በግልጽ አቀራረብ እና መረጃውን በሚቀበል ገጽ እንጀምራለን።',
        },
        {
          q: 'የማህበራዊ ሚዲያችንን በየወሩ ማስተዳደር ትችላላችሁ?',
          a: 'አዎ። በቀላሉ ሊረዱት የሚችሉትን ሪፖርት ያካተተ ወርሃዊ እቅድ በማዘጋጀት፣ ዲዛይን በማድረግ እና በመፖሰት ማስተዳደር እንችላለን።',
        },
      ],
    },
  },
  {
    slug: 'it-consultancy',
    icon: Cpu,
    gradient: 'from-slate-500/10 to-gray-500/10',
    iconGradient: 'from-slate-600 to-gray-600',
    hoverBorder: 'hover:border-slate-300/50 group-hover:shadow-slate-500/10',
    accent: 'from-slate-600 to-gray-600',
    images: {
      hero: img('3184465'),
      heroPosition: 'object-[center_25%]',
      heroAlt: {
        en: 'IT consultants in a meeting with a client about a technology plan',
        am: 'የአይቲ አማካሪዎች ከደንበኛ ጋር ስለ ቴክኖሎጂ እቅድ በስብሰባ ላይ',
      },
      gallery: [
        {
          src: img('3184291'),
          position: 'object-[center_20%]',
          alt: { en: 'Workshop on digital systems and work process', am: 'ስለ ዲጂታል ስርዓቶች እና የስራ ሂደት ስልጠና' },
          caption: { en: 'Workshops with the people who use the systems', am: 'ስርዓቱን ከሚጠቀሙ ሰዎች ጋር የሚደረግ ስልጠና' },
        },
        {
          src: img('3183150'),
          position: 'object-[center_20%]',
          alt: { en: 'On-site technology consulting discussion', am: 'በቦታው የቴክኖሎጂ አማካሪነት ውይይት' },
          caption: { en: 'Clear options explained in plain language', am: 'በግልጽ ቋንቋ የሚብራሩ አማራጮች' },
        },
        {
          src: img('3183197'),
          position: 'object-center',
          alt: { en: 'Team reviewing a technology roadmap around a table', am: 'ቡድን የቴክኖሎጂ የመንገድ ካርታ በጠረጴዛ ላይ እየገመገመ' },
          caption: { en: 'A written sequence you can implement in stages', am: 'በደረጃ መተግበር የሚችሉት የጽሑፍ ቅደም ተከተል' },
        },
      ],
    },
    en: {
      title: 'IT Consultancy',
      shortDesc: 'Expert advice on technology solutions and strategies',
      tagline: 'Independent advice on what to build, buy, or fix before you spend the budget.',
      overview: [
        'IT consultancy is for organizations that need a decision, not another tool they will not use. We help you choose between a website, an app, an off-the-shelf system, a CCTV setup, or a mix, based on how your people actually work and what you can maintain.',
        'We review current software, hosting, security habits, and the gaps between departments. Then we give a written recommendation: what to do first, what can wait, what to stop, and a realistic sequence. If we implement the work ourselves, the plan and the build stay aligned.',
        'Typical engagements include digital-transformation roadmaps, system selection, performance and security reviews, staff training plans, and integration advice so new software does not sit beside old spreadsheets forever.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'Owners who have been quoted several systems and need a clear comparison',
        'Clinics, schools, and offices moving from paper to digital records',
        'Companies whose current site or software is slow, unused, or risky',
        'Teams that need training and process, not only installation',
      ],
      includedTitle: 'What is included',
      included: [
        'Discovery interviews with the people who use the systems',
        'Review of current tools, hosting, and security basics',
        'Written options with pros, cons, and effort',
        'A phased roadmap you can fund in stages',
        'Vendor-neutral advice, including when not to custom-build',
        'Implementation oversight if we or a partner do the work',
        'Training outline for the staff who will live with the system',
        'Follow-up session after the first changes go live',
      ],
      processTitle: 'How consultancy works',
      process: [
        { title: 'Scope', text: 'We agree on the decision you need and the parts of the business involved.' },
        { title: 'Listen', text: 'We speak with management and with the people doing the daily work.' },
        { title: 'Assess', text: 'We inspect tools, gaps, risks, and quick wins.' },
        { title: 'Recommend', text: 'You receive a plain document with a recommended path and alternatives.' },
        { title: 'Decide', text: 'We walk through the choice so budget and timeline are honest.' },
        { title: 'Support delivery', text: 'If you proceed, we can implement or supervise the next phase.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'Fewer expensive tools bought for the wrong problem',
        'A sequence of work that matches cash flow and staff capacity',
        'Clarity for management and for the team that will use the system',
        'A partner that can also build, host, and support what you choose',
      ],
      toolsTitle: 'How we work',
      tools: ['Process mapping', 'System audits', 'Security reviews', 'Roadmaps', 'Vendor comparison', 'Training plans'],
      duration: '1–3 weeks',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Scope and interviews', duration: '2–4 days', detail: 'We agree the decision you need and speak with the people who use the systems.' },
        { phase: 'Assessment', duration: '4–7 days', detail: 'We review current tools, hosting, security, and the gaps between teams.' },
        { phase: 'Written recommendation', duration: '3–5 days', detail: 'You receive options, a recommended path, and a sequence you can implement.' },
        { phase: 'Decision support', duration: '1–3 days', detail: 'We walk through the choice so the next step is clear before any build begins.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'The decision you need to make, in one sentence if possible',
        'Access to current software, hosting, and any previous quotes or documents',
        'Time with both management and the staff who do the daily work',
        'A list of what is working, what is failing, and what must not change',
        'Any compliance, budget window, or deadline that affects the sequence',
        'One person who can confirm facts and approve the recommended path',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'Is consultancy separate from building the system?',
          a: 'It can be. Some clients only want the plan. Others ask us to implement the recommended website, app, or CCTV work afterward.',
        },
        {
          q: 'Will you always recommend a custom system?',
          a: 'No. If an existing product is enough, we say so. Custom work is for when the ready-made options cannot cover the operation.',
        },
        {
          q: 'Can you work with systems we already have?',
          a: 'Yes. Integration and improvement of what you already paid for is often the better first step.',
        },
      ],
    },
    am: {
      title: 'የIT አማካሪነት (IT Consultancy)',
      shortDesc: 'በቴክኖሎጂ መፍትሔዎች እና ስልቶች ላይ የሚሰጥ የባለሙያ ምክር',
      tagline: 'ባጀትዎን ከማውጣትዎ በፊት ምን መገንባት፣ መግዛት ወይም ማስተካከል እንዳለብዎት የሚሰጥ ገለልተኛ ምክር።',
      overview: [
        'የIT አማካሪነት አገልግሎት የማይጠቀሙበት ሌላ መሳሪያ ሳይሆን ውሳኔ ለሚያስፈልጋቸው ድርጅቶች የተዘጋጀ ነው። ሰዎችዎ በእርግጥ በሚሰሩበት መንገድ እና እርስዎ ማስተዳደር በሚችሉት አቅም ላይ በመመስረት በዌብሳይት፣ በመተግበሪያ፣ በተዘጋጀ ሶፍትዌር፣ በCCTV ካሜራ ተከላ ወይም በተደባለቀ መፍትሔ መካከል እንዲመርጡ እንረዳዎታለን።',
        'የአሁኑን ሶፍትዌር፣ ሆስቲንግ፣ የደህንነት ልምዶች እና በክፍሎች መካከል ያሉ ክፍተቶችን እንገመግማለን። በመቀጠል በጽሁፍ የተደገፈ ምክረ ሀሳብ እናቀርባለን፡ ቅድሚያ የሚሰጠው፣ ሊቆይ የሚችለው፣ መቆም ያለበት እና እውነተኛ የቅደም ተከተል እቅድ። ሥራውን እኛ የምንተገብረው ከሆነ እቅዱ እና ግንባታው የተጣጣሙ ሆነው ይቀጥላሉ።',
        'የተለመዱ ሥራዎቻችን የዲጂታል ትራንስፎርሜሽን እቅዶችን፣ የስርዓት መረጣዎችን፣ የአፈጻጸም እና ደህንነት ግምገማዎችን፣ የሰራተኞች ሥልጠና እቅዶችን እና አዲስ ሶፍትዌር ከድሮ ኤክሴሎች ጋር ተቀምጦ እንዳይቀር የሚያስችሉ የትስስር ምክሮችን ያካትታሉ።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን ነው?',
      audience: [
        'የተለያዩ ሶፍትዌሮች ተቅጣፊ ቀርቦላቸው ግልጽ ንጽጽር የሚያስፈልጋቸው ባለቤቶች',
        'ከወረቀት አሰራር ወደ ዲጂታል መዝገብ የሚሸጋገሩ ክሊኒኮች፣ ትምህርት ቤቶች እና ቢሮዎች',
        'የአሁኑ ዌብሳይታቸው ወይም ሶፍትዌራቸው ቀርፋፋ፣ የማይ ጥቅም ወይም ለአደጋ የተጋለጠ የሆኑባቸው ኩባንያዎች',
        'ከተከላ ባለፈ ሥልጠና እና የአሰራር ሂደት የሚያስፈልጋቸው ቡድኖች',
      ],
      includedTitle: 'ምን ያካተተ ነው?',
      included: [
        'ስርዓቶቹን ከሚጠቀሙ ሰዎች ጋር የሚደረግ የምርመራ ቃለ-መጠይቅ',
        'የአሁኑ መሣሪያዎች፣ ሆስቲንግ እና የመሠረታዊ ደህንነት ግምገማ',
        'ጥቅምና ጉዳቶችን እንዲሁም የሚያስፈልገውን ድካም ያካተተ በጽሁፍ የቀረበ አማራጭ',
        'በደረጃ በደረጃ በጀት ሊመደብለት የሚችል የትግበራ እቅድ (roadmap)',
        'ከአንድ ወገን ያልወገነ ምክር (በተለይም ብጁ ሶፍትዌር መስራት ሳያስፈልግ ሲቀር)',
        'እኛ ወይም አጋሮቻችን ሥራውን ስንሰራ የሚደረግ የትግበራ ቁጥጥር',
        'ከስርዓቱ ጋር አብረው ለሚኖሩ ሰራተኞች የሚሆን የሥልጠና ረቂቅ',
        'የመጀመሪያዎቹ ለውጦች ወደ ሥራ ከገቡ በኋላ የሚደረግ የክትትል ውይይት',
      ],
      processTitle: 'የአማካሪነት አገልግሎት እንዴት ይሰራል?',
      process: [
        { title: 'ወሰን መወሰን (Scope)', text: 'በሚያስፈልግዎት ውሳኔ እና በሚመለከታቸው የንግድ ክፍሎች ላይ እንስማማለን።' },
        { title: 'ማዳመጥ', text: 'ከአመራሩ እና የእለት ተእለት ስራውን ከሚያከናውኑ ሰራተኞች ጋር እንነጋገራለን።' },
        { title: 'መገምገም', text: 'መሣሪያዎችን፣ ክፍተቶችን፣ ስጋቶችን እና ፈጣን መፍትሔዎችን እንመረምራለን።' },
        { title: 'ምክረ ሀሳብ ማቅረብ', text: 'የተመከረውን መንገድ እና አማራጮችን ያካተተ ግልጽ ሰነድ ይረከባሉ።' },
        { title: 'መወሰን', text: 'በጀቱ እና የጊዜ ሰሌዳው እውነተኛ እንዲሆኑ በምርጫው ላይ አብረን እንወያያለን።' },
        { title: 'ትግበራን መደገፍ', text: 'ወደፊት የሚቀጥሉ ከሆነ ቀጣዩን ምዕራፍ መተግበር ወይም መከታተል እንችላለን።' },
      ],
      benefitsTitle: 'ምን ያተርፋሉ?',
      benefits: [
        'ለተሳሳተ ችግር የሚወጡ ውድ የወጪ መሣሪያዎች መቀነስ',
        'የገንዘብ ፍሰትዎን እና የሰራተኞችን አቅም ያገናዘበ የሥራ ቅደም ተከተል',
        'ለአመራሩ እና ስርዓቱን ለሚጠቀመው ቡድን የሚሆን ግልጽነት',
        'በመረጡት መፍትሔ ላይ መገንባት፣ ማስተናገድ (hosting) እና ድጋፍ መስጠት የሚችል አጋር',
      ],
      toolsTitle: 'እንዴት እንሰራለን?',
      tools: ['Process mapping', 'System audits', 'Security reviews', 'Roadmaps', 'Vendor comparison', 'Training plans'],
      duration: 'ከ1 እስከ 3 ሳምንታት',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'ወሰን እና ቃለ-መጠይቆች', duration: 'ከ2–4 ቀናት', detail: 'በሚያስፈልግዎት ውሳኔ ላይ እንስማማለን፣ እንዲሁም ስርዓቶቹን ከሚጠቀሙ ሰዎች ጋር እንነጋገራለን።' },
        { phase: 'ግምገማ', duration: 'ከ4–7 ቀናት', detail: 'የአሁኑን መሣሪያዎች፣ ሆስቲንግ፣ ደህንነት እና በቡድኖች መካከል ያሉ ክፍተቶችን እንገመግማለን።' },
        { phase: 'የጽሁፍ ምክረ ሀሳብ', duration: 'ከ3–5 ቀናት', detail: 'አማራጮችን፣ የተመከረውን መንገድ እና ሊተገበር የሚችል ቅደም ተከተል ይቀበላሉ።' },
        { phase: 'የውሳኔ ድጋፍ', duration: 'ከ1–3 ቀናት', detail: 'ምንም አይነት ግንባታ ከመጀመሩ በፊት ቀጣዩ እርምጃ ግልጽ እንዲሆን በምርጫው ላይ እንወያያለን።' },
      ],
      requirementsTitle: 'ሥራውን ለመጀመር ከእርስዎ የምንፈልጋቸው ነገሮች',
      requirements: [
        'ማድረግ የሚፈልጉት ውሳኔ (ከተቻለ በአንድ አረፍተ ነገር)',
        'የአሁኑ ሶፍትዌር፣ ሆስቲንግ እና ማንኛውም የቀደሙ የዋጋ ተመኖች ወይም ሰነዶች መዳረሻ (Access)',
        'ከአመራሩ እና የእለት ተእለት ስራውን ከሚያከናውኑ ሰራተኞች ጋር የሚሆን ጊዜ',
        'ምን እየሰራ እንደሆነ፣ ምን እየተበላሸ እንደሆነ እና ምን መለወጥ እንደሌለበት የሚያሳይ ዝርዝር',
        'በቅደም ተከተሉ ላይ ተጽእኖ የሚያሳድር ማንኛውም ህጋዊ ግዴታ፣ የበጀት ወሰን ወይም የጊዜ ገደብ',
        'እውነታዎችን ማረጋገጥ እና የተመከረውን መንገድ ማጽደቅ የሚችል አንድ ሰው',
      ],
      faqsTitle: 'የተለመዱ ጥያቄዎች',
      faqs: [
        {
          q: 'የአማካሪነት አገልግሎቱ ስርዓቱን ከመገንባት የተለየ ነው?',
          a: 'ሊሆን ይችላል። ചില ደንበኞች እቅዱን ብቻ ይፈልጋሉ። ሌሎች ደግሞ የተመከረውን ዌብሳይት፣ መተግበሪያ ወይም የCCTV ሥራ በመቀጠል እንድንሰራላቸው ይጠይቁናል።',
        },
        {
          q: 'ሁልጊዜ ብጁ (custom) ስርዓት እንድንሰራ ነው የምትመክሩት?',
          a: 'አይደለም። ነባር ዝግጁ ምርት በቂ ከሆነ እሱን እንመክራለን። ብጁ ሥራ የሚያስፈልገው ዝግጁ አማራጮች የድርጅቱን የሥራ ሂደት ሙሉ በሙሉ ማሸፈን ሲያቅታቸው ብቻ ነው።',
        },
        {
          q: 'አሁን ካሉን ነባር ስርዓቶች ጋር አብራችሁ መስራት ትችላላችሁ?',
          a: 'አዎ። ቀድሞውኑ ወጪ የወጣበትን ስርዓት ማቀናጀት እና ማሻሻል አብዛኛውን ጊዜ የተሻለው የመጀመሪያ እርምጃ ነው።',
        },
      ],
    },
  },
  {
    slug: 'cctv-camera-installation',
    icon: Cctv,
    gradient: 'from-amber-500/10 to-yellow-500/10',
    iconGradient: 'from-amber-600 to-yellow-500',
    hoverBorder: 'hover:border-amber-300/50 group-hover:shadow-amber-500/10',
    accent: 'from-amber-600 to-yellow-500',
    images: {
      hero: img('430208'),
      heroPosition: 'object-center',
      heroAlt: {
        en: 'White CCTV dome camera installed on a building ceiling',
        am: 'በህንጻ ጣሪያ ላይ የተተከለ ነጭ የCCTV ዶም ካሜራ',
      },
      gallery: [
        {
          src: img('279810'),
          position: 'object-center',
          alt: { en: 'Outdoor CCTV camera covering a site entrance', am: 'የቦታ መግቢያን የምትሸፍን የውጭ CCTV ካሜራ' },
          caption: { en: 'Cameras aimed at doors, parking, and stores', am: 'ወደ በሮች፣ መኪና ማቆሚያ እና መደብሮች የሚያመለክቱ ካሜራዎች' },
        },
        {
          src: img('96612'),
          position: 'object-center',
          alt: { en: 'Security camera mounted for property surveillance', am: 'ንብረትን ለመከታተል የተገጠመ የደህንነት ካሜራ' },
          caption: { en: 'Indoor and outdoor cameras in one system', am: 'የውስጥ እና የውጭ ካሜራዎች በአንድ ስርዓት' },
        },
        {
          src: img('1148820'),
          position: 'object-[center_30%]',
          alt: { en: 'Surveillance camera watching a building exterior', am: 'የህንጻ ውጭን የምትከታተል ካሜራ' },
          caption: { en: 'Live view and playback set up on your phone', am: 'በስልክዎ ላይ የሚዘጋጅ ቀጥታ እይታ እና መልሶ ማጫወት' },
        },
      ],
    },
    en: {
      title: 'CCTV Camera Installation',
      shortDesc: 'Professional CCTV camera installation, configuration, remote viewing setup, and maintenance for offices, homes, clinics, and commercial buildings.',
      tagline: 'Cameras placed where they matter, recorded reliably, and viewable on your phone.',
      overview: [
        'Fortino Tech installs and configures CCTV systems for homes, offices, clinics, shops, warehouses, and commercial buildings. The job is not only mounting a camera. We plan coverage for doors, corridors, parking, cash points, and storage, then install the recorders, cabling, power, and network so the system actually records when you need it.',
        'After installation we set up remote viewing so you can check live video and playback from your phone. We explain how many days of recording you will keep, how night vision behaves, and who should have access to the footage.',
        'We also offer maintenance, camera replacement, and support when a view goes dark or a recorder fills up. Warranty, maintenance, and technical consultation are part of keeping the system useful after the installers leave.',
      ],
      audienceTitle: 'Who this service is for',
      audience: [
        'Homes and apartment compounds that need visible, reliable coverage',
        'Offices, clinics, and shops that must watch entrances and service areas',
        'Warehouses and commercial buildings with multiple floors or gates',
        'Sites that already have cameras but cannot view them remotely or have blind spots',
      ],
      includedTitle: 'What is included',
      included: [
        'Site walk-through and camera-position plan',
        'Supply and installation of indoor and outdoor cameras as specified',
        'NVR/DVR setup, storage calculation, and recording schedule',
        'Cabling, power, and tidy routing where the building allows',
        'Network configuration for stable recording and remote access',
        'Phone and computer viewing setup with user accounts',
        'Handover: how to live-view, play back, and export a clip',
        'Optional maintenance visits and 24/7 support',
      ],
      processTitle: 'How we install CCTV',
      process: [
        { title: 'Survey', text: 'We walk the site, note lighting, power, and the points you must see.' },
        { title: 'Propose', text: 'You receive a camera count, types, storage days, and an installation schedule.' },
        { title: 'Install', text: 'We mount cameras, run cable, and fit the recorder with care for the building.' },
        { title: 'Configure', text: 'We set motion or continuous recording, users, and remote viewing.' },
        { title: 'Prove it', text: 'We show live views and playback with you before we leave.' },
        { title: 'Maintain', text: 'We remain available for cleaning, replacements, and extra cameras.' },
      ],
      benefitsTitle: 'What you gain',
      benefits: [
        'Footage you can actually find when an incident happens',
        'Fewer blind spots because positions are planned, not guessed',
        'Remote checks without calling someone on site',
        'A system that can be expanded instead of ripped out later',
      ],
      toolsTitle: 'What we set up',
      tools: ['IP cameras', 'NVR / DVR', 'Night vision', 'Remote viewing', 'PoE networking', 'Maintenance plans'],
      duration: '1–7 days',
      timelineTitle: 'Typical timeline',
      timeline: [
        { phase: 'Site survey', duration: 'Half a day', detail: 'We walk the property, note lighting, power, internet, and the points you must see.' },
        { phase: 'Plan and equipment list', duration: '1 day', detail: 'You receive camera count, types, storage days, and an installation schedule.' },
        { phase: 'Installation', duration: '1–4 days', detail: 'A home or small office is usually one day. A larger building can take several days.' },
        { phase: 'Setup, training, and handover', duration: 'Half a day', detail: 'We configure recording, phone viewing, and show you live view, playback, and clip export.' },
      ],
      requirementsTitle: 'What we need from you to implement',
      requirements: [
        'Access to the building on the agreed installation days',
        'A list of areas that must be covered: doors, parking, cash desk, stores, corridors',
        'Power points near camera routes, or permission to add them',
        'A working internet connection if you want live viewing on your phone',
        'A lockable place for the recorder, preferably indoors and ventilated',
        'Who should have the viewing app login, and who should not',
      ],
      faqsTitle: 'Common questions',
      faqs: [
        {
          q: 'Can I watch the cameras on my phone?',
          a: 'Yes. Remote viewing on your phone is part of a standard installation, as long as the site has a working internet connection.',
        },
        {
          q: 'Do you work in homes as well as offices?',
          a: 'Yes. We install for homes, offices, clinics, and commercial buildings, and we size the system to the property.',
        },
        {
          q: 'What if a camera fails later?',
          a: 'Contact us. We provide warranty service where it applies, plus maintenance, support, and technical consultation to keep the system running.',
        },
      ],
    },
   am: {
      title: 'የሲሲቲቪ (CCTV) ካሜራ ገጠማ',
      shortDesc: 'ለቢሮዎች፣ ለመኖሪያ ቤቶች፣ ክሊኒኮች እና ለንግድ ሕንፃዎች አስተማማኝ የሲሲቲቪ ካሜራ ገጠማ፣ ኮንፊገሬሽን፣ በስልክ የመከታተያ ሲስተም እና የጥገና አገልግሎት።',
      tagline: 'ካሜራዎች በደህንነት ወሳኝ በሆኑ ቦታዎች ላይ ተገጥመው፣ በአስተማማኝ ሁኔታ የሚቀርፁ እና በስልክዎ በቀላሉ የሚታዩ።',
      overview: [
        'ፎርቲኖ ቴክ (Fortino Tech) ለመኖሪያ ቤቶች፣ ቢሮዎች፣ ክሊኒኮች፣ ሱቆች፣ መጋዘኖች እና የንግድ ሕንፃዎች የሲሲቲቪ ሲስተሞችን ይገጥማል፣ ያስተካክላልም። ስራችን ካሜራ መሰቀል ብቻ አይደለም፤ ለደጃፎች፣ ኮሪደሮች፣ የመኪና ማቆሚያዎች፣ የገንዘብ መክፈያ ቦታዎች እና መጋዘኖች የተሟላ ሽፋን እንዲኖር እናቅዳለን። በመቀጠልም በምትፈልጉበት ጊዜ ሲስተሙ በትክክል መቅረፁን ለማረጋገጥ ሪኮርደሮችን፣ ገመዶችን፣ የኤሌክትሪክ እና የኔትወርክ መስመሮችን እንገጥማለን።',
        'ከገጠማው በኋላ የቀጥታ ስርጭት እና የተቀረፁ ተንቀሳቃሽ ምስሎችን (playback) በስልክዎ መከታተል እንዲችሉ የስልክ ማያያዝ ስራ እንሰራለን። ስንት ቀን የተቀረፀ ምስል ማቆየት እንደሚችሉ፣ የሌሊት እይታ (night vision) እንዴት እንደሚሰራ እና ለማን የቪዲዮ መዳረሻ ፈቃድ መሰጠት እንዳለበት በግልጽ እንያስረዳለን።',
        'ከዚህም በተጨማሪ ካሜራዎች በሚጨልሙበት ወይም ሪኮርደር በሚሞላበት ጊዜ የጥገና፣ የካሜራ ቅያሬ እና የቴክኒክ ድጋፍ አገልግሎት እንሰጣለን። ገጣሚዎች ከሄዱ በኋላም ሲስተሙ ሁልጊዜ ጥቅም ላይ እንዲውል የዋስትና፣ የጥገና እና የቴክኒክ ምክር አገልግሎቶችን እናቀርባለን።',
      ],
      audienceTitle: 'ይህ አገልግሎት ለማን የተዘጋጀ ነው',
      audience: [
        'ግልጽ እና አስተማማኝ የደህንነት ሽፋን የሚያስፈልጋቸው ቤቶች እና የአፓርትመንት ግቢዎች',
        'የመግቢያ በሮችን እና የአገልግሎት መስጫ ቦታዎችን መቆጣጠር የሚፈልጉ ቢሮዎች፣ ክሊኒኮች እና ሱቆች',
        'ባለብዙ ፎቅ ወይም ብዙ በሮች ያሏቸው መጋዘኖች እና የንግድ ሕንፃዎች',
        'ቀደም ሲል የተገጠሙ ካሜራዎች ያሏቸው ነገር ግን በስልክ መከታተል ለማይችሉ ወይም የካሜራ እይታ ያልሸፈናቸው ቦታዎች (blind spots) ላሏቸው',
      ],
      includedTitle: 'አገልግሎቱ የሚያካተታቸው',
      included: [
        'የቦታው ቅኝት እና የካሜራ ቦታዎች እቅድ ማውጣት',
        'በተገለፀው መሰረት የውስጥ እና የውጭ ካሜራዎችን ማቅረብ እና መገጠም',
        'የ NVR/DVR ቅንብር፣ የማከማቻ መጠን (storage) ስሌት እና የምስል ቀረጻ ፕሮግራም ማስተካከል',
        'ህንፃው በሚፈቅደው መልኩ ገመዶችን እና የኤሌክትሪክ መስመሮችን በጥራጥሬ እና በጽዳት መዘርጋት',
        'ለአስተማማኝ ቀረጻ እና በስልክ ለመከታተል የሚረዳ የኔትወርክ ኮንፊገሬሽን',
        'ከተጠቃሚ መለያዎች (user accounts) ጋር የሞባይል እና የኮምፒውተር መከታተያ ማስተካከል',
        'ርክክብ፡ የቀጥታ ምስል እንዴት እንደሚታይ፣ የተቀረፀ እንደሚከፈት እና ቪዲዮ ኤክስፖርት እንደሚደረግ ማሳየት',
        'አማራጭ የየጊዜው የጥገና ጉብኝቶች እና የ24/7 የቴክኒክ ድጋፍ',
      ],
      processTitle: 'የሲሲቲቪ ካሜራ ገጠማ ሂደታችን',
      process: [
        { title: 'ቅኝት', text: 'ቦታውን እንጎበኛለን፤ መብራት፣ ኤሌክትሪክ እና መታየት ያለባቸውን ዋና ዋና ቦታዎች እንለያለን።' },
        { title: 'እቅድ እና ዋጋ ማቅረብ', text: 'የካሜራ ብዛት፣ አይነት፣ የተቀረፀ ምስል የሚቆይበት ቀናት እና የገጠማ ፕሮግራም ይደርስዎታል።' },
        { title: 'ገጠማ', text: 'ለህንፃው ጥንቃቄ በማድረግ ካሜራዎችን እንሰቅላለን፣ ገመዶችን እንዘረጋለን፣ ሪኮርደሩንም እንገጥማለን።' },
        { title: 'ማስተካከል (Configure)', text: 'የእንቅስቃሴ ወይም የእንቅስቃሴ-ሜላ ቀረጻ፣ የተጠቃሚ መለያዎችን እና የርቀት እይታን እናስተክላለን።' },
        { title: 'ማረጋገጥ', text: 'ከመሄዳችን በፊት የቀጥታ እይታዎችን እና የተቀረፁ ምስሎችን አብረንዎት እናረጋግጣለን።' },
        { title: 'ጥገና', text: 'ለጽዳት፣ ለካሜራ ቅያሬዎች እና ለተጨማሪ ካሜራዎች ገጠማ ሁልጊዜ ዝግጁ ነን።' },
      ],
      benefitsTitle: 'የሚያገኙት ጥቅም',
      benefits: [
        'አደጋ ወይም አጋጣሚ በሚፈጠርበት ጊዜ በቀላሉ የሚገኙ የተቀረፁ ምስሎች',
        'ቦታዎች በታቀደ እንጂ በግምት ስላልተመረጡ አነስተኛ ያልተሸፈኑ ቦታዎች (blind spots)',
        'በቦታው የሚገኝ ሰውን ሳይደውሉ በስልክዎ የርቀት ቁጥጥር ማድረግ መቻል',
        'በኋላ ላይ ሙሉ በሙሉ ሳይነቀል ሊሰፋ እና ሊሻሻል የሚችል ሲስተም',
      ],
      toolsTitle: 'የምንገጥማቸው እና የምንጠቀማቸው',
      tools: ['የ IP ካሜራዎች', 'NVR / DVR', 'የሌሊት እይታ (Night vision)', 'የስልክ/ርቀት እይታ', 'PoE ኔትወርክ', 'የጥገና እቅዶች'],
      duration: 'ከ1–7 நாட்கள்',
      timelineTitle: 'መደበኛ የጊዜ ሰሌዳ',
      timeline: [
        { phase: 'የቦታ ቅኝት', duration: 'እስከ እኩለ ቀን', detail: 'ቦታውን እንጎበኛለን፤ መብራት፣ ኤሌክትሪክ፣ ኢንተርኔት እና መታየት ያለባቸውን ቦታዎች እንለያለን።' },
        { phase: 'እቅድ እና የእቃ ዝርዝር', duration: '1 ቀን', detail: 'የካሜራ ብዛት፣ አይነት፣ የተቀረፀ ምስል የሚቆይበት ቀናት እና የገጠማ ፕሮግራም ይደርስዎታል።' },
        { phase: 'የገጠማ ስራ', duration: 'ከ1–4 நாட்கள்', detail: 'የቤት ወይም ትንሽ ቢሮ ገጠማ አብዛኛውን ጊዜ አንድ ቀን ይወስዳል። ትላልቅ ሕንፃዎች ግን ጥቂት ቀናትን ሊወስዱ ይችላሉ።' },
        { phase: 'ቅንብር፣ ስልጠና እና ርክክብ', duration: 'እስከ እኩለ ቀን', detail: 'ቀረጻውን እና የስልክ እይታውን እናስተክላለን፤ የቀጥታ እይታ፣ የተቀረፀ ምስል ከፋፈት እና ቪዲዮ ማውጣትን እናሰለጥናለን።' },
      ],
      requirementsTitle: 'ስራውን ለመጀመር ከእርስዎ የሚፈለጉ ነገሮች',
      requirements: [
        'በተስማማንባቸው የገጠማ ቀናት ወደ ህንፃው የመግባት ፈቃድ',
        'መሸፈን ያለባቸው ቦታዎች ዝርዝር፡ በሮች፣ የመኪና ማቆሚያ፣ የገንዘብ መክፈያ፣ ዕቃ ቤቶች፣ ኮሪደሮች',
        'በካሜራ መስመሮች አቅራቢያ የኤሌክትሪክ ሶኬቶች ወይም አዲስ ለመጨመር ፈቃድ',
        'በስልክዎ የቀጥታ ምስል መከታተል የሚፈልጉ ከሆነ የሚሰራ የኢንተርኔት መስመር',
        'ለሪኮርደሩ የሚሆን ደህንነቱ የተጠበቀ፣ በተሻለ ሁኔታ የቤት ውስጥ እና አየር የሚገባበት ቦታ',
        'የስልክ መከታተያ መተግበሪያው የመግቢያ መለያ (login) ማግኘት ያለበት እና የሌለበት ሰው ስም ዝርዝር',
      ],
      faqsTitle: 'ተደጋግመው የሚጠየቁ ጥያቄዎች',
      faqs: [
        {
          q: 'ካሜራዎቹን በስልኬ መከታተል እችላለሁ?',
          a: 'አዎ። ቦታው ላይ የሚሰራ የኢንተርኔት መስመር እስካለ ድረስ በስልክዎ የርቀት እይታ ማስተካከል የመደበኛ ገጠማችን አካል ነው።',
        },
        {
          q: 'ከቢሮዎች በተጨማሪ ለመኖሪያ ቤቶችም አገልግሎት ትሰጣላችሁ?',
          a: 'አዎ። ለመኖሪያ ቤቶች፣ ቢሮዎች፣ ክሊኒኮች እና ለንግድ ሕንፃዎች እንገጥማለን፤ ሲስተሙንም እንደ ቦታው ስፋት እንመጥነዋለን።',
        },
        {
          q: 'በኋላ ላይ ካሜራ ቢበላሽስ?',
          a: 'ያነጋግሩን። ሲስተሙ በጥሩ ሁኔታ መስራቱን እንዲቀጥል የሚመለከተውን የዋስትና አገልግሎት፣ የጥገና እና የቴክኒክ ድጋፍ እናቀርባለን።',
        },
      ],
    },
  },
]

export function getServiceCopy(service, language) {
  const raw = language === 'am' ? service.am : service.en
  return {
    title: raw.title,
    shortDesc: raw.shortDesc,
    tagline: raw.tagline,
    overview: raw.overview || raw.overview || [],
    audienceTitle: raw.audienceTitle || raw.audienceTitle,
    audience: raw.audience || [],
    includedTitle: raw.includedTitle || raw.includedTitle,
    included: raw.included || [],
    processTitle: raw.processTitle || raw.processTitle,
    process: (raw.process || []).map((step) => ({
      title: step.title,
      text: step.text || step.text,
    })),
    benefitsTitle: raw.benefitsTitle || raw.benefitsTitle,
    benefits: raw.benefits || [],
    toolsTitle: raw.toolsTitle || raw.toolsTitle,
    tools: raw.tools || [],
    duration: raw.duration || '',
    timelineTitle: raw.timelineTitle || raw.timelineTitle,
    timeline: raw.timeline || [],
    requirementsTitle: raw.requirementsTitle || raw.requirementsTitle,
    requirements: raw.requirements || [],
    faqsTitle: raw.faqsTitle || raw.faqsTitle,
    faqs: raw.faqs || [],
  }
}

export function getServiceBySlug(slug) {
  return SERVICES.find((service) => service.slug === slug) || null
}

export function getRelatedServices(slug, limit = 3) {
  return SERVICES.filter((service) => service.slug !== slug).slice(0, limit)
}
